import { FC, ReactNode } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type { CardOwnerPassenger, SavedCustomerCard } from '../../api/customerCards/cardHelpers';
import SavedCardOptionList, { PAGE_OPTION } from './SavedCardOptionList';

export { PAGE_OPTION };

/**
 * Everything the saved-card picker shows, already decided. The container
 * ({@link ./SavedCardPicker}) owns the form state, loading, permissions and every
 * guard; this renders them and reports the agent's clicks back.
 */
export type SavedCardPickerViewProps = {
    disabled?: boolean;

    /** Financial receipt only: the agent picks the paying passenger here. */
    showPayerSelect: boolean;
    passengers: CardOwnerPassenger[];
    payerPassengerId: number | string | null;
    onPayerChange: (passengerId: string | null) => void;
    payerSelectDisabled: boolean;
    payerSelectTooltip: string;
    payerHelperText: string;

    /** The payer's cards. */
    cards: SavedCustomerCard[];
    /** {@link PAGE_OPTION} or the chosen card's id. */
    selectedValue: string;
    onSelect: (value: string) => void;
    listTooltip: string;
    cardsDisabled: boolean;
    cardDisabledReason: (card: SavedCustomerCard) => string;

    loading: boolean;
    loadError: string;
    showEmpty: boolean;

    /** A saved card is chosen. */
    cardChosen: boolean;
    paymentOptions: number[];
    numberOfPayments: number;
    onNumberOfPaymentsChange: (count: number) => void;
    paymentsTooltip: string;
    /** True for an ILS receipt with a card chosen — credit (קרדיט, with
     *  interest) is the ILS-side counterpart of the plain USD split
     *  (tourRegistrationApp's canCreditDeal), but Pelecard's saved-card
     *  token-debit endpoint has no credit mode, so it's shown as explicitly
     *  unavailable rather than silently missing. */
    showCreditUnavailable: boolean;

    askCvvAndHolderId: boolean;
    cvv2: string;
    onCvv2Change: (value: string) => void;
    holderIdNumber: string;
    onHolderIdNumberChange: (value: string) => void;
};

const SavedCardPickerView: FC<SavedCardPickerViewProps> = ({
    disabled,
    showPayerSelect,
    passengers,
    payerPassengerId,
    onPayerChange,
    payerSelectDisabled,
    payerSelectTooltip,
    payerHelperText,
    cards,
    selectedValue,
    onSelect,
    listTooltip,
    cardsDisabled,
    cardDisabledReason,
    loading,
    loadError,
    showEmpty,
    cardChosen,
    paymentOptions,
    numberOfPayments,
    onNumberOfPaymentsChange,
    paymentsTooltip,
    showCreditUnavailable,
    askCvvAndHolderId,
    cvv2,
    onCvv2Change,
    holderIdNumber,
    onHolderIdNumberChange
}) => {
    const cardControls: ReactNode = cardChosen ? (
        <>
            <Tooltip title={paymentsTooltip} placement="top-start">
                <Box component="span" sx={{ display: 'block', maxWidth: 320 }}>
                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="Number of payments"
                        value={numberOfPayments}
                        onChange={(event) => onNumberOfPaymentsChange(Number(event.target.value) || 1)}
                        disabled={disabled || paymentOptions.length <= 1}
                    >
                        {paymentOptions.map((count) => (
                            <MenuItem key={count} value={count}>
                                {count === 1 ? '1 (single payment)' : count}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            </Tooltip>

            {/* ILS's counterpart to the USD plain split is a credit deal
                (קרדיט, with interest) — tourRegistrationApp offers it, but
                Pelecard's saved-card token-debit endpoint has no credit mode
                (see chargeSavedCard.ts), so it's called out as unavailable
                rather than silently missing. */}
            {showCreditUnavailable && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    A credit deal (קרדיט, with interest) is not available on a saved card — use the payment page
                    instead.
                </Typography>
            )}

            {/* Built in now so switching it on after the terminal test needs no rework. */}
            {askCvvAndHolderId && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    <TextField
                        size="small"
                        label="CVV"
                        value={cvv2}
                        onChange={(event) => onCvv2Change(event.target.value.replace(/\D/g, '').slice(0, 4))}
                        inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                        disabled={disabled}
                        required
                        sx={{ width: 110 }}
                    />
                    <TextField
                        size="small"
                        label="Card holder ID"
                        value={holderIdNumber}
                        onChange={(event) =>
                            onHolderIdNumberChange(event.target.value.replace(/\D/g, '').slice(0, 9))
                        }
                        inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                        disabled={disabled}
                        required
                        sx={{ width: 200 }}
                    />
                </Box>
            )}
        </>
    ) : null;

    return (
        <Box sx={{ mt: 1.5 }}>
            {showPayerSelect && (
                <Tooltip title={payerSelectTooltip} placement="top-start">
                    <Box component="span" sx={{ display: 'block' }}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Paying passenger"
                            value={payerPassengerId ?? ''}
                            onChange={(event) => onPayerChange(event.target.value || null)}
                            disabled={payerSelectDisabled}
                            sx={{ mb: 1 }}
                            helperText={payerHelperText}
                        >
                            {passengers.map((passenger) => (
                                <MenuItem key={passenger.passengerId} value={passenger.passengerId}>
                                    {passenger.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </Tooltip>
            )}

            <Tooltip title={listTooltip} placement="top-start">
                <Box>
                    <SavedCardOptionList
                        cards={cards}
                        value={selectedValue}
                        onChange={onSelect}
                        pageOptionDisabled={!!disabled}
                        cardsDisabled={cardsDisabled}
                        cardDisabledReason={cardDisabledReason}
                    />
                </Box>
            </Tooltip>

            {loading && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 1.5, mt: 0.5 }}>
                    Loading saved cards…
                </Typography>
            )}
            {!loading && loadError && (
                <Typography variant="caption" color="error" display="block" sx={{ px: 1.5, mt: 0.5 }}>
                    {loadError}
                </Typography>
            )}
            {showEmpty && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 1.5, mt: 0.5 }}>
                    No saved cards for this passenger
                </Typography>
            )}

            {cardChosen && <Box sx={{ mt: 1 }}>{cardControls}</Box>}
        </Box>
    );
};

export default SavedCardPickerView;
